import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import {
	Badge,
	Box,
	Button,
	Card,
	CardBody,
	CardHeader,
	Container,
	Divider,
	FormControl,
	FormErrorMessage,
	FormLabel,
	HStack,
	IconButton,
	Input,
	Modal,
	ModalBody,
	ModalCloseButton,
	ModalContent,
	ModalFooter,
	ModalHeader,
	ModalOverlay,
	Select,
	SimpleGrid,
	Stack,
	Table,
	TableContainer,
	Tbody,
	Td,
	Text,
	Textarea,
	Th,
	Thead,
	Tr,
	useDisclosure,
	useToast,
	VStack,
} from "@chakra-ui/react";
import { FiDownload, FiEdit2, FiPlus, FiSave, FiTrash2, FiUpload } from "react-icons/fi";

import Dropzone from "../../Components/Dropzone/Dropzone";
import { PropertyConfirmModal } from "../../Components/PropertyModal/shared";
import CustomOwnerPlansTableRow from "../../Components/TableRows/CustomOwnerPlansTableRow";
import {
	addAgentAsync,
	addCustomOwnerPlanAsync,
	addProviderAsync,
	deleteAgencyAssociationAsync,
	getDashboardAsync,
	removeProviderAsync,
	selectActiveProperties,
	updateAgencyAsync,
	updateAssociationAsync,
	updateProviderAsync,
	updateTemplatesAsync,
} from "../../redux/user/userSlice";
import { _arrayBufferToBase64 } from "../../utils/convertors";
import { API, getHeaders, setHeaders } from "../../utils/http";
import { isValidIban, normalizeIban } from "../../utils/verifier";

function Profile() {
	const user = useSelector((state) => state.user.user);
	const properties = useSelector(selectActiveProperties);
	const associations = user.associations;
	const providers = useSelector((state) => state.user.user?.providers) || [];
	const { t } = useTranslation();
	const customOwnerPlans = user.customOwnerPlans || [];
	const initialCustomOwnerPlanInfo = {
		name: "",
		description: "",
		price: {
			amount: "",
			currency: "ron",
		},
	};
	const [editOwnerPlanInfo, setEditOwnerPlanInfo] = useState(initialCustomOwnerPlanInfo);
	const { isOpen, onOpen, onClose } = useDisclosure();
	const { isOpen: isUploadTemplateModalOpen, onOpen: onUploadTemplateModalOpen, onClose: onUploadTemplateModalClose } = useDisclosure();
	const { isOpen: isAddOwnerPlanModalOpen, onOpen: onAddOwnerPlanModalOpen, onClose: onAddOwnerPlanModalClose } = useDisclosure();
	const { isOpen: isProviderModalOpen, onOpen: onProviderModalOpen, onClose: onProviderModalClose } = useDisclosure();
	const { isOpen: isDeleteProviderOpen, onOpen: onDeleteProviderOpen, onClose: onDeleteProviderClose } = useDisclosure();
	const { isOpen: isAssociationEditOpen, onOpen: onAssociationEditOpen, onClose: onAssociationEditClose } = useDisclosure();
	const { isOpen: isDeleteAssociationOpen, onOpen: onDeleteAssociationOpen, onClose: onDeleteAssociationClose } = useDisclosure();
	const initialProviderInformation = { id: null, name: "", iban: "" };
	const initialAssociationEdit = {
		id: null,
		name: "",
		administratorName: "",
		email: "",
		phoneNumber: "",
		iban: "",
		description: "",
	};
	const [providerInformation, setProviderInformation] = useState(initialProviderInformation);
	const [providerToDelete, setProviderToDelete] = useState(null);
	const [providerError, setProviderError] = useState({});
	const [associationInformation, setAssociationInformation] = useState(initialAssociationEdit);
	const [associationToDelete, setAssociationToDelete] = useState(null);
	const [associationError, setAssociationError] = useState({});
	const [associationSaving, setAssociationSaving] = useState(false);
	const [ibanError, setIbanError] = useState(false);
	const [providerSaving, setProviderSaving] = useState(false);
	const [isEditing, setIsEditing] = useState(false);
	const initialDocumentInformation = { documentData: "", documentName: "", contentType: "" };
	const [selectedTemplateType, setSelectedTemplateType] = useState("");
	const [documentInformation, setDocumentInformation] = useState(initialDocumentInformation);
	const templateTypes = [
		{ id: "disposition", label: "Disposition" },
		{ id: "dispositionSigned", label: "Disposition Signed" },
		{ id: "combinedAdministrationFee", label: "Combined Administration Fee" },
		{ id: "administrationFee", label: "Administration Fee" },
		// { id: "TICKET", label: "Ticket" },
		// { id: "TICKET_SIGNED", label: "Ticket Signed" },
	];
	const initialBillingInformation = {
		name: user?.billingInformation.name,
		tradeRegisterNumber: user?.billingInformation.tradeRegisterNumber,
		identificationNumber: user?.billingInformation.identificationNumber,
		address: user?.billingInformation.address,
		iban: user?.billingInformation.iban,
		bank: user?.billingInformation.bank,
		vat: user?.billingInformation.vat,
		admFeeSeries: user?.billingInformation.admFeeSeries,
		ticketSeries: user?.billingInformation.ticketSeries,
	};
	const templates = user?.templates;
	const [billingInformation, setBillingInformation] = useState(initialBillingInformation);

	const initialAgentInformation = { email: "" };
	const [agentInformation, setAgentInformation] = useState(initialAgentInformation);
	const toast = useToast();
	const dispatch = useDispatch();
	const handleAgentChange = (e) => {
		setAgentInformation({ ...agentInformation, [e.target.name]: e.target.value });
	};
	const handleBillingInformationChange = (e) => {
		if (e.target.name === "iban" && ibanError) setIbanError(false);
		setBillingInformation({ ...billingInformation, [e.target.name]: e.target.value });
	};
	const handleProviderChange = (e) => {
		if (providerError[e.target.name]) {
			setProviderError({ ...providerError, [e.target.name]: false });
		}
		setProviderInformation({ ...providerInformation, [e.target.name]: e.target.value });
	};
	const openAddProvider = () => {
		setProviderInformation(initialProviderInformation);
		setProviderError({});
		onProviderModalOpen();
	};
	const openEditProvider = (provider) => {
		setProviderInformation({ id: provider.id, name: provider.name || "", iban: provider.iban || "" });
		setProviderError({});
		onProviderModalOpen();
	};
	const saveProvider = () => {
		const name = String(providerInformation.name || "").trim();
		const iban = normalizeIban(providerInformation.iban);
		const errors = {};
		if (!name) errors.name = true;
		if (!iban || !isValidIban(iban)) errors.iban = true;
		if (Object.keys(errors).length) {
			setProviderError(errors);
			toast({
				title: "Error encountered.",
				description: errors.iban ? t("profile.invalidIban") : "Invalid information",
				status: "error",
				duration: 2000,
				isClosable: true,
			});
			return;
		}
		setProviderError({});
		setProviderSaving(true);
		const payload = { name, iban };
		const action = providerInformation.id
			? updateProviderAsync({ id: providerInformation.id, ...payload })
			: addProviderAsync(payload);
		dispatch(action)
			.then((r) => {
				if (r.error?.message) {
					toast({
						title: "Error encountered",
						description: r.error.message,
						status: "error",
						duration: 9000,
						isClosable: true,
					});
				} else {
					toast({
						title: "Success!",
						description: t("profile.providerSaved"),
						status: "success",
						duration: 2000,
						isClosable: true,
					});
					onProviderModalClose();
					setProviderInformation(initialProviderInformation);
				}
			})
			.finally(() => {
				setProviderSaving(false);
			});
	};
	const confirmDeleteProvider = (provider) => {
		setProviderToDelete(provider);
		onDeleteProviderOpen();
	};
	const deleteProvider = () => {
		if (!providerToDelete?.id) return;
		setProviderSaving(true);
		dispatch(removeProviderAsync({ id: providerToDelete.id }))
			.then((r) => {
				if (r.error?.message) {
					toast({
						title: "Error encountered",
						description: r.error.message,
						status: "error",
						duration: 9000,
						isClosable: true,
					});
				} else {
					toast({
						title: "Success!",
						description: t("profile.providerDeleted"),
						status: "success",
						duration: 2000,
						isClosable: true,
					});
				}
			})
			.finally(() => {
				setProviderSaving(false);
				setProviderToDelete(null);
				onDeleteProviderClose();
			});
	};
	const handleAssociationChange = (e) => {
		if (associationError[e.target.name]) {
			setAssociationError({ ...associationError, [e.target.name]: false });
		}
		setAssociationInformation({ ...associationInformation, [e.target.name]: e.target.value });
	};
	const openEditAssociation = (association) => {
		setAssociationInformation({
			id: association.id,
			name: association.name || "",
			administratorName: association.administratorName || "",
			email: association.email || "",
			phoneNumber: association.phoneNumber || "",
			iban: association.iban || "",
			description: association.description || "",
		});
		setAssociationError({});
		onAssociationEditOpen();
	};
	const saveAssociation = () => {
		const name = String(associationInformation.name || "").trim();
		const iban = normalizeIban(associationInformation.iban);
		const errors = {};
		if (!name) errors.name = true;
		if (iban && !isValidIban(iban)) errors.iban = true;
		if (Object.keys(errors).length) {
			setAssociationError(errors);
			toast({
				title: "Error encountered.",
				description: errors.iban ? t("profile.invalidIban") : "Invalid information",
				status: "error",
				duration: 2000,
				isClosable: true,
			});
			return;
		}
		setAssociationError({});
		setAssociationSaving(true);
		dispatch(
			updateAssociationAsync({
				id: associationInformation.id,
				name,
				administratorName: String(associationInformation.administratorName || "").trim(),
				email: String(associationInformation.email || "").trim(),
				phoneNumber: String(associationInformation.phoneNumber || "").trim(),
				iban,
				description: String(associationInformation.description || "").trim(),
			})
		)
			.then((r) => {
				if (r.error?.message) {
					toast({
						title: "Error encountered",
						description: r.error.message,
						status: "error",
						duration: 9000,
						isClosable: true,
					});
				} else {
					toast({
						title: "Success!",
						description: t("profile.associationSaved"),
						status: "success",
						duration: 2000,
						isClosable: true,
					});
					onAssociationEditClose();
					setAssociationInformation(initialAssociationEdit);
				}
			})
			.finally(() => {
				setAssociationSaving(false);
			});
	};
	const confirmDeleteAssociation = (association) => {
		setAssociationToDelete(association);
		onDeleteAssociationOpen();
	};
	const deleteAssociation = () => {
		if (!associationToDelete?.id) return;
		setAssociationSaving(true);
		dispatch(deleteAgencyAssociationAsync({ id: associationToDelete.id }))
			.then((r) => {
				if (r.error?.message) {
					toast({
						title: "Error encountered",
						description: r.error.message,
						status: "error",
						duration: 9000,
						isClosable: true,
					});
				} else {
					toast({
						title: "Success!",
						description: t("profile.associationDeleted"),
						status: "success",
						duration: 2000,
						isClosable: true,
					});
				}
			})
			.finally(() => {
				setAssociationSaving(false);
				setAssociationToDelete(null);
				onDeleteAssociationClose();
			});
	};
	const onDocumentDrop = async (acceptedFiles) => {
		console.log(acceptedFiles);
		const buffer = await acceptedFiles[0].arrayBuffer();
		const documentName = acceptedFiles[0].name;
		const contentType = acceptedFiles[0].type;
		setDocumentInformation({ ...documentInformation, documentData: _arrayBufferToBase64(buffer), documentName, contentType });
	};
	const addAgent = () => {
		const payload = { ...agentInformation, agencyEmail: user.email };
		console.log(payload);
		dispatch(addAgentAsync(payload))
			.then((r) => {
				if (r.error?.message)
					toast({
						title: "Error encountered",
						description: r.error.message,
						status: "error",
						duration: 9000,
						isClosable: true,
					});
				else {
					toast({
						title: "Success!",
						description: "Agent added!",
						status: "success",
						duration: 2000,
						isClosable: true,
					});
				}
			})
			.finally(() => {
				setAgentInformation(initialAgentInformation);
				onClose();
			});
	};
	const addCustomOwnerPlan = () => {
		const payload = {
			...editOwnerPlanInfo,
		};
		console.log(payload);

		dispatch(addCustomOwnerPlanAsync(payload))
			.then((r) => {
				if (r.error?.message)
					toast({
						title: "Error encountered",
						description: r.error.message,
						status: "error",
						duration: 9000,
						isClosable: true,
					});
				else {
					toast({
						title: "Success!",
						description: "Custom plan added!",
						status: "success",
						duration: 2000,
						isClosable: true,
					});
				}
			})
			.finally(() => {
				setEditOwnerPlanInfo(initialCustomOwnerPlanInfo);
				onAddOwnerPlanModalClose();
			});
	};
	const handleExportAssociations = () => {
		// Define the header row
		const header = "Name,Description,IBAN,Administrator Name,Email,Phone Number\n";

		// Create a csv file from the associations
		const csv = associations
			.map((a) => {
				// Wrap each field value in double quotes to handle commas
				return `"${a.name}","${a.description}","${a.iban}","${a.administratorName}","${a.email}","${a.phoneNumber}"\n`;
			})
			.join("");

		// Concatenate the header and data
		const csvData = header + csv;

		const blob = new Blob([csvData], { type: "text/csv" });
		const url = window.URL.createObjectURL(blob);
		const a = document.createElement("a");
		a.setAttribute("hidden", "");
		a.setAttribute("href", url);
		a.setAttribute("download", "associations.csv");
		document.body.appendChild(a);
		a.click();
		document.body.removeChild(a);
	};

	const configureStripeAccount = () => {
		console.log("Configure stripe");
		API.post("create-connected-account").then((r) => {
			console.log(r.data);
			window.open(r.data.onboardingUrl, "_self");
		});
	};
	const goToStripeDashboard = () => {
		API.post("stripe-dashboard", { stripeConnectedAccountId: user.stripeConnectedAccountId }).then((r) => {
			console.log(r.data);
			window.open(r.data.loginLink, "_blank");
		});
	};
	const handleUploadTemplateType = (type) => {
		console.log(type);
		dispatch(updateTemplatesAsync({ templateType: type, ...documentInformation }))
			.then((r) => {
				if (r.error?.message)
					toast({
						title: "Error encountered",
						description: r.error.message,
						status: "error",
						duration: 9000,
						isClosable: true,
					});
				else {
					toast({
						title: "Success!",
						description: "Template uploaded!",
						status: "success",
						duration: 2000,
						isClosable: true,
					});
				}
			})
			.finally(() => {
				onUploadTemplateModalClose();
			});
		// API.put("upload-template", { templateType: type, ...documentInformation }).then((r) => {
		// 	console.log(r.data);
		// 	// setBillingInformation({ ...billingInformation, templates: { ...billingInformation.templates, [type]: r.data } });
		// 	// setDocumentInformation(initialDocumentInformation);
		// });
	};
	const handleSave = () => {
		const iban = normalizeIban(billingInformation.iban);
		if (iban && !isValidIban(iban)) {
			setIbanError(true);
			toast({
				title: "Error encountered.",
				description: t("profile.invalidIban"),
				status: "error",
				duration: 2000,
				isClosable: true,
			});
			return false;
		}
		setIbanError(false);
		dispatch(updateAgencyAsync({ type: "billingInformation", ...billingInformation, iban }));
		return true;
	};
	const renderAddAgentModal = () => (
		<Modal isOpen={isOpen} onClose={onClose} size="lg" isCentered>
			<ModalOverlay bg="blackAlpha.300" backdropFilter="blur(10px)" />
			<ModalContent>
				<ModalHeader borderBottom="1px solid" borderColor="gray.100">
					<Text fontSize="xl">Add an Agent</Text>
				</ModalHeader>
				<ModalCloseButton />
				<ModalBody py={6}>
					<FormControl>
						<FormLabel>Email Address</FormLabel>
						<Input type="email" name="email" placeholder="Enter agent's email" value={agentInformation.email} onChange={handleAgentChange} />
					</FormControl>
				</ModalBody>
				<ModalFooter borderTop="1px solid" borderColor="gray.100">
					<Button variant="ghost" onClick={onClose} mr={3}>
						Cancel
					</Button>
					<Button colorScheme="blue" leftIcon={<FiPlus />} onClick={addAgent}>
						Add Agent
					</Button>
				</ModalFooter>
			</ModalContent>
		</Modal>
	);
	const renderProviderModal = () => (
		<Modal
			isOpen={isProviderModalOpen}
			onClose={() => {
				setProviderInformation(initialProviderInformation);
				setProviderError({});
				onProviderModalClose();
			}}
			size="lg"
			isCentered
		>
			<ModalOverlay bg="blackAlpha.300" backdropFilter="blur(10px)" />
			<ModalContent>
				<ModalHeader borderBottom="1px solid" borderColor="gray.100">
					<Text fontSize="xl">{providerInformation.id ? t("profile.editProvider") : t("profile.addProvider")}</Text>
				</ModalHeader>
				<ModalCloseButton />
				<ModalBody py={6}>
					<VStack spacing={4} align="stretch">
						<FormControl isInvalid={providerError.name}>
							<FormLabel>{t("profile.providerName")}</FormLabel>
							<Input
								name="name"
								placeholder={t("profile.providerNamePlaceholder")}
								value={providerInformation.name}
								onChange={handleProviderChange}
							/>
						</FormControl>
						<FormControl isInvalid={providerError.iban}>
							<FormLabel>{t("property.iban")}</FormLabel>
							<Input
								name="iban"
								placeholder={t("property.ibanPlaceholder")}
								value={providerInformation.iban}
								onChange={handleProviderChange}
							/>
							<FormErrorMessage>{t("profile.invalidIban")}</FormErrorMessage>
						</FormControl>
					</VStack>
				</ModalBody>
				<ModalFooter borderTop="1px solid" borderColor="gray.100">
					<Button
						variant="ghost"
						onClick={() => {
							setProviderInformation(initialProviderInformation);
							setProviderError({});
							onProviderModalClose();
						}}
						mr={3}
					>
						Cancel
					</Button>
					<Button colorScheme="blue" leftIcon={<FiSave />} onClick={saveProvider} isLoading={providerSaving}>
						{t("misc.save")}
					</Button>
				</ModalFooter>
			</ModalContent>
		</Modal>
	);
	const navigate = useNavigate();
	const [onBehalfOf, setOnBehalfOf] = useState("");
	const handleChangeOnBehalfOf = (e) => {
		setOnBehalfOf(e.target.value);
	};
	const handleChangeOwnerPlanInformation = (e) => {
		const { name, value } = e.target;
		setEditOwnerPlanInfo((prev) => ({ ...prev, [name]: value }));
	};
	const saveOnBehalfOf = () => {
		setHeaders({ ...getHeaders(), onbehalfof: onBehalfOf });
		dispatch(getDashboardAsync())
			.then((r) => {
				console.log(r);
				if (r.error?.message)
					toast({
						title: "Error encountered",
						description: r.error.message,
						status: "error",
						duration: 9000,
						isClosable: true,
					});
				else {
					toast({
						title: "Success!",
						description: "Dashboard info success",
						status: "success",
						duration: 2000,
						isClosable: true,
					});
				}
			})
			.finally(() => {
				navigate(`/`);
			});
	};
	const { isOpen: isOnBehalfOfOpen, onOpen: onOnBehalfOfOpen, onClose: onOnBehalfOfClose } = useDisclosure();

	const setRenderOnBehalfOfModal = () => (
		<Modal isOpen={isOnBehalfOfOpen} onClose={onOnBehalfOfClose} size="lg" isCentered>
			<ModalOverlay bg="blackAlpha.300" backdropFilter="blur(10px)" />
			<ModalContent>
				<ModalHeader borderBottom="1px solid" borderColor="gray.100">
					<Text fontSize="xl">Set On Behalf Of</Text>
				</ModalHeader>
				<ModalCloseButton />
				<ModalBody py={6}>
					<FormControl>
						<FormLabel>Email Address</FormLabel>
						<Input type="email" placeholder="Enter email address" value={onBehalfOf} onChange={handleChangeOnBehalfOf} />
					</FormControl>
				</ModalBody>
				<ModalFooter borderTop="1px solid" borderColor="gray.100">
					<Button variant="ghost" onClick={onOnBehalfOfClose} mr={3}>
						Cancel
					</Button>
					<Button colorScheme="blue" onClick={saveOnBehalfOf}>
						Save Changes
					</Button>
				</ModalFooter>
			</ModalContent>
		</Modal>
	);
	const renderAddOwmerPlanModal = () => {
		return (
			<Modal isOpen={isAddOwnerPlanModalOpen} onClose={onAddOwnerPlanModalClose} size="lg" isCentered>
				<ModalOverlay bg="blackAlpha.300" backdropFilter="blur(10px)" />
				<ModalContent>
					<ModalHeader borderBottom="1px solid" borderColor="gray.100">
						<Text fontSize="xl">Add Owner Plan</Text>
					</ModalHeader>
					<ModalCloseButton />
					<ModalBody py={6}>
						<VStack spacing={4}>
							<FormControl>
								<FormLabel>Plan Name</FormLabel>
								<Input type="text" name="name" placeholder="Enter plan name" value={editOwnerPlanInfo.name} onChange={handleChangeOwnerPlanInformation} />
							</FormControl>
							<FormControl>
								<FormLabel>Description</FormLabel>
								<Textarea
									name="description"
									placeholder="Enter plan description"
									value={editOwnerPlanInfo.description}
									onChange={handleChangeOwnerPlanInformation}
									rows={4}
								/>
							</FormControl>
							<FormControl>
								<FormLabel>Price</FormLabel>
								<HStack spacing={4}>
									<Input
										type="number"
										name="amount"
										placeholder="Amount"
										value={editOwnerPlanInfo.price.amount}
										onChange={(e) => {
											setEditOwnerPlanInfo((prev) => ({
												...prev,
												price: { ...prev.price, amount: e.target.value },
											}));
										}}
										flex={1}
									/>
									<Select
										value={editOwnerPlanInfo.price.currency}
										onChange={(e) => {
											setEditOwnerPlanInfo((prev) => ({
												...prev,
												price: { ...prev.price, currency: e.target.value },
											}));
										}}
										width="100px"
									>
										<option value="ron">RON</option>
										<option value="eur">EUR</option>
									</Select>
								</HStack>
							</FormControl>
						</VStack>
					</ModalBody>
					<ModalFooter borderTop="1px solid" borderColor="gray.100">
						<Button variant="ghost" onClick={onAddOwnerPlanModalClose} mr={3}>
							Cancel
						</Button>
						<Button colorScheme="blue" leftIcon={<FiPlus />} onClick={addCustomOwnerPlan}>
							Add Plan
						</Button>
					</ModalFooter>
				</ModalContent>
			</Modal>
		);
	};

	const renderUploadTemplateModal = () => (
		<Modal isOpen={isUploadTemplateModalOpen} onClose={onUploadTemplateModalClose} size="lg" isCentered>
			<ModalOverlay bg="blackAlpha.300" backdropFilter="blur(10px)" />
			<ModalContent>
				<ModalHeader borderBottom="1px solid" borderColor="gray.100">
					<Text fontSize="xl">Upload Template</Text>
				</ModalHeader>
				<ModalCloseButton />
				<ModalBody py={6}>
					<Dropzone
						onDrop={onDocumentDrop}
						accept={{
							"application/vnd.openxmlformats-officedocument.wordprocessingml.document": [".docx"],
							"application/msword": [".doc"],
						}}
						description="Upload DOC or DOCX Format"
					/>
				</ModalBody>
				<ModalFooter borderTop="1px solid" borderColor="gray.100">
					<Button variant="ghost" onClick={onUploadTemplateModalClose} mr={3}>
						Cancel
					</Button>
					<Button
						colorScheme="blue"
						leftIcon={<FiUpload />}
						onClick={() => {
							handleUploadTemplateType(selectedTemplateType);
						}}
					>
						Upload Template
					</Button>
				</ModalFooter>
			</ModalContent>
		</Modal>
	);

	const renderAssociationEditModal = () => (
		<Modal
			isOpen={isAssociationEditOpen}
			onClose={() => {
				setAssociationInformation(initialAssociationEdit);
				setAssociationError({});
				onAssociationEditClose();
			}}
			size="lg"
			isCentered
		>
			<ModalOverlay bg="blackAlpha.300" backdropFilter="blur(10px)" />
			<ModalContent>
				<ModalHeader borderBottom="1px solid" borderColor="gray.100">
					<Text fontSize="xl">{t("profile.editAssociation")}</Text>
				</ModalHeader>
				<ModalCloseButton />
				<ModalBody py={6}>
					<VStack spacing={4} align="stretch">
						<FormControl isInvalid={associationError.name} isRequired>
							<FormLabel>{t("table.name")}</FormLabel>
							<Input name="name" value={associationInformation.name} onChange={handleAssociationChange} />
						</FormControl>
						<FormControl>
							<FormLabel>{t("property.administratorName")}</FormLabel>
							<Input name="administratorName" value={associationInformation.administratorName} onChange={handleAssociationChange} />
						</FormControl>
						<SimpleGrid columns={2} spacing={4}>
							<FormControl>
								<FormLabel>{t("property.email")}</FormLabel>
								<Input name="email" value={associationInformation.email} onChange={handleAssociationChange} />
							</FormControl>
							<FormControl>
								<FormLabel>{t("property.phoneNumber")}</FormLabel>
								<Input name="phoneNumber" value={associationInformation.phoneNumber} onChange={handleAssociationChange} />
							</FormControl>
						</SimpleGrid>
						<FormControl isInvalid={associationError.iban}>
							<FormLabel>{t("property.iban")}</FormLabel>
							<Input name="iban" value={associationInformation.iban} onChange={handleAssociationChange} placeholder={t("property.ibanPlaceholder")} />
							<FormErrorMessage>{t("profile.invalidIban")}</FormErrorMessage>
						</FormControl>
						<FormControl>
							<FormLabel>{t("property.description")}</FormLabel>
							<Textarea name="description" value={associationInformation.description} onChange={handleAssociationChange} rows={3} />
						</FormControl>
					</VStack>
				</ModalBody>
				<ModalFooter borderTop="1px solid" borderColor="gray.100">
					<Button
						variant="ghost"
						mr={3}
						onClick={() => {
							setAssociationInformation(initialAssociationEdit);
							setAssociationError({});
							onAssociationEditClose();
						}}
					>
						Cancel
					</Button>
					<Button colorScheme="blue" leftIcon={<FiSave />} onClick={saveAssociation} isLoading={associationSaving}>
						{t("misc.save")}
					</Button>
				</ModalFooter>
			</ModalContent>
		</Modal>
	);

	return (
		<Container maxW="container.xl" py={8}>
			<Box width="100%" mb={6} hidden={user.email !== "omihai00@gmail.com"}>
				<Button colorScheme="blue" leftIcon={<FiEdit2 />} onClick={onOnBehalfOfOpen}>
					Set on behalf of
				</Button>
			</Box>

			<Stack spacing={6}>
				{/* First row - 2 columns */}
				<SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6}>
					{/* Profile Information Card */}
					<Card>
						<CardHeader>
							<Text fontSize="xl" fontWeight="bold">
								Profile Information
							</Text>
						</CardHeader>
						<CardBody>
							<VStack align="stretch" spacing={4}>
								<HStack justify="space-between">
									<Text fontWeight="medium">Name</Text>
									<Text color="gray.600">{user?.name || "Not provided"}</Text>
								</HStack>
								<HStack justify="space-between">
									<Text fontWeight="medium">Phone Number</Text>
									<Text color="gray.600">{user?.phoneNumber || "Not provided"}</Text>
								</HStack>
								<HStack justify="space-between">
									<Text fontWeight="medium">Email</Text>
									<Text color="gray.600">{user?.email}</Text>
								</HStack>
								<Divider />
								<Box>
									<Text fontSize="lg" fontWeight="bold" mb={4}>
										Agents
									</Text>
									{user.agents?.length === 0 ? (
										<Text color="gray.500">No agents added yet</Text>
									) : (
										<VStack align="stretch" spacing={2}>
											{user.agents.map((agent, index) => (
												<HStack key={index} p={2} bg="gray.50" rounded="md">
													<Text flex={1}>{agent}</Text>
													<Badge colorScheme="green">Active</Badge>
												</HStack>
											))}
										</VStack>
									)}
								</Box>
								<HStack spacing={4} mt={4}>
									{user.stripeConnectedAccountId ? (
										<Button colorScheme="green" onClick={goToStripeDashboard} size="sm" variant="solid">
											Payment Dashboard
										</Button>
									) : (
										<Button colorScheme="yellow" onClick={configureStripeAccount} isDisabled={user.type !== "agency"} size="sm">
											Configure Payment Account
										</Button>
									)}
									<Button colorScheme="blue" onClick={onOpen} isDisabled={user.type !== "agency"} size="sm" leftIcon={<FiPlus />}>
										Add Agent
									</Button>
								</HStack>
							</VStack>
						</CardBody>
					</Card>

					{/* Billing Information Card */}
					<Card>
						<CardHeader>
							<HStack justify="space-between">
								<Text fontSize="xl" fontWeight="bold">
									Billing Information
								</Text>
								<Button
									size="sm"
									colorScheme={isEditing ? "green" : "blue"}
									leftIcon={isEditing ? <FiSave /> : <FiEdit2 />}
									onClick={() => {
										if (isEditing) {
											if (!handleSave()) return;
										}
										setIsEditing(!isEditing);
									}}
									isDisabled={user.type !== "agency"}
								>
									{isEditing ? "Save Changes" : "Edit"}
								</Button>
							</HStack>
						</CardHeader>
						<CardBody>
							<SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
								<FormControl size="sm">
									<FormLabel fontSize="sm">Company Name</FormLabel>
									{isEditing ? (
										<Input size="sm" value={billingInformation.name} name="name" onChange={handleBillingInformationChange} />
									) : (
										<Text fontSize="sm" color="gray.600">
											{user?.billingInformation.name || "Not provided"}
										</Text>
									)}
								</FormControl>
								<FormControl size="sm">
									<FormLabel fontSize="sm">Trade Register Number</FormLabel>
									{isEditing ? (
										<Input size="sm" value={billingInformation.tradeRegisterNumber} name="tradeRegisterNumber" onChange={handleBillingInformationChange} />
									) : (
										<Text fontSize="sm" color="gray.600">
											{user?.billingInformation.tradeRegisterNumber || "Not provided"}
										</Text>
									)}
								</FormControl>
								<FormControl size="sm">
									<FormLabel fontSize="sm">CIF</FormLabel>
									{isEditing ? (
										<Input size="sm" value={billingInformation.identificationNumber} name="identificationNumber" onChange={handleBillingInformationChange} />
									) : (
										<Text fontSize="sm" color="gray.600">
											{user?.billingInformation.identificationNumber || "Not provided"}
										</Text>
									)}
								</FormControl>
								<FormControl size="sm">
									<FormLabel fontSize="sm">VAT</FormLabel>
									{isEditing ? (
										<Input size="sm" value={billingInformation.vat} name="vat" onChange={handleBillingInformationChange} />
									) : (
										<Text fontSize="sm" color="gray.600">
											{user?.billingInformation.vat || "Not provided"}
										</Text>
									)}
								</FormControl>
								<FormControl size="sm" isInvalid={ibanError}>
									<FormLabel fontSize="sm">IBAN</FormLabel>
									{isEditing ? (
										<>
											<Input size="sm" value={billingInformation.iban} name="iban" onChange={handleBillingInformationChange} />
											<FormErrorMessage>{t("profile.invalidIban")}</FormErrorMessage>
										</>
									) : (
										<Text fontSize="sm" color="gray.600">
											{user?.billingInformation.iban || "Not provided"}
										</Text>
									)}
								</FormControl>
								<FormControl size="sm">
									<FormLabel fontSize="sm">Bank</FormLabel>
									{isEditing ? (
										<Input size="sm" value={billingInformation.bank} name="bank" onChange={handleBillingInformationChange} />
									) : (
										<Text fontSize="sm" color="gray.600">
											{user?.billingInformation.bank || "Not provided"}
										</Text>
									)}
								</FormControl>
								<FormControl size="sm" gridColumn={{ md: "1 / -1" }}>
									<FormLabel fontSize="sm">Address</FormLabel>
									{isEditing ? (
										<Textarea size="sm" value={billingInformation.address} name="address" onChange={handleBillingInformationChange} rows={2} />
									) : (
										<Text fontSize="sm" color="gray.600">
											{user?.billingInformation.address || "Not provided"}
										</Text>
									)}
								</FormControl>
								<Divider gridColumn={{ md: "1 / -1" }} />
								<FormControl size="sm">
									<FormLabel fontSize="sm">Administration Fee Series</FormLabel>
									{isEditing ? (
										<Input size="sm" value={billingInformation.admFeeSeries} name="admFeeSeries" onChange={handleBillingInformationChange} />
									) : (
										<Text fontSize="sm" color="gray.600">
											{user?.billingInformation.admFeeSeries || "Not provided"}
										</Text>
									)}
								</FormControl>
								<FormControl size="sm">
									<FormLabel fontSize="sm">Ticket Series</FormLabel>
									{isEditing ? (
										<Input size="sm" value={billingInformation.ticketSeries} name="ticketSeries" onChange={handleBillingInformationChange} />
									) : (
										<Text fontSize="sm" color="gray.600">
											{user?.billingInformation.ticketSeries || "Not provided"}
										</Text>
									)}
								</FormControl>
							</SimpleGrid>
						</CardBody>
					</Card>
				</SimpleGrid>

				{/* Second row - Templates + Providers */}
				<SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6}>
					{/* Templates Card */}
					<Card>
						<CardHeader>
							<Text fontSize="xl" fontWeight="bold">
								Templates
							</Text>
						</CardHeader>
						<CardBody>
							<VStack align="stretch" spacing={4}>
								{templateTypes.map((t) => (
									<HStack key={t.id} justify="space-between" p={3} bg="gray.50" rounded="md">
										<VStack align="start" spacing={1}>
											<Text fontWeight="medium">{t.label}</Text>
											<Text fontSize="sm" color="gray.600">
												{templates[t.id]?.documentName || "No template uploaded"}
											</Text>
										</VStack>
										<Button
											isDisabled={user.type !== "agency"}
											size="sm"
											leftIcon={<FiUpload />}
											onClick={() => {
												setSelectedTemplateType(t.id);
												onUploadTemplateModalOpen();
											}}
										>
											Upload
										</Button>
									</HStack>
								))}
							</VStack>
						</CardBody>
					</Card>

					{/* Providers Card */}
					<Card>
						<CardHeader>
							<HStack justify="space-between">
								<Text fontSize="xl" fontWeight="bold">
									{t("profile.providers")}
								</Text>
								<Button size="sm" colorScheme="blue" leftIcon={<FiPlus />} onClick={openAddProvider}>
									{t("profile.addProvider")}
								</Button>
							</HStack>
						</CardHeader>
						<CardBody>
							<Box maxH="300px" overflowY="auto" pr={2}>
								{providers.length === 0 ? (
									<Text color="gray.500">{t("profile.noProviders")}</Text>
								) : (
									<TableContainer>
										<Table size="sm" variant="simple">
											<Thead>
												<Tr>
													<Th>{t("profile.providerName")}</Th>
													<Th>{t("property.iban")}</Th>
													<Th></Th>
												</Tr>
											</Thead>
											<Tbody>
												{providers.map((provider) => (
													<Tr key={provider.id}>
														<Td>{provider.name}</Td>
														<Td fontFamily="mono" fontSize="xs">
															{provider.iban}
														</Td>
														<Td textAlign="right">
															<HStack spacing={1} justify="flex-end">
																<IconButton
																	aria-label={t("profile.editProvider")}
																	icon={<FiEdit2 />}
																	size="xs"
																	variant="ghost"
																	onClick={() => openEditProvider(provider)}
																/>
																<IconButton
																	aria-label={t("misc.delete")}
																	icon={<FiTrash2 />}
																	size="xs"
																	variant="ghost"
																	colorScheme="red"
																	onClick={() => confirmDeleteProvider(provider)}
																/>
															</HStack>
														</Td>
													</Tr>
												))}
											</Tbody>
										</Table>
									</TableContainer>
								)}
							</Box>
						</CardBody>
					</Card>
				</SimpleGrid>

				{/* Associations - full width for long names / IBANs */}
				<Card>
					<CardHeader>
						<HStack justify="space-between">
							<Text fontSize="xl" fontWeight="bold">
								{t("profile.associations")}
							</Text>
							<Button variant="outline" size="sm" leftIcon={<FiDownload />} onClick={handleExportAssociations}>
								Export to CSV
							</Button>
						</HStack>
					</CardHeader>
					<CardBody>
						<Box maxH="300px" overflowY="auto" pr={2}>
							{!associations?.length ? (
								<Text color="gray.500">{t("profile.noAssociations")}</Text>
							) : (
								<TableContainer>
									<Table size="sm" variant="simple">
										<Thead>
											<Tr>
												<Th>{t("table.name")}</Th>
												<Th>{t("property.iban")}</Th>
												<Th></Th>
											</Tr>
										</Thead>
										<Tbody>
											{associations.map((association) => (
												<Tr key={association.id}>
													<Td>
														<Text fontSize="sm">{association.name}</Text>
														{association.administratorName ? (
															<Text fontSize="xs" color="gray.500">
																{association.administratorName}
															</Text>
														) : null}
													</Td>
													<Td fontFamily="mono" fontSize="xs">
														{association.iban || "-"}
													</Td>
													<Td textAlign="right">
														<HStack spacing={1} justify="flex-end">
															<IconButton
																aria-label={t("profile.editAssociation")}
																icon={<FiEdit2 />}
																size="xs"
																variant="ghost"
																onClick={() => openEditAssociation(association)}
															/>
															<IconButton
																aria-label={t("misc.delete")}
																icon={<FiTrash2 />}
																size="xs"
																variant="ghost"
																colorScheme="red"
																onClick={() => confirmDeleteAssociation(association)}
															/>
														</HStack>
													</Td>
												</Tr>
											))}
										</Tbody>
									</Table>
								</TableContainer>
							)}
						</Box>
					</CardBody>
				</Card>

				{/* Owner Plans - full width */}
				<Card>
					<CardHeader>
						<HStack justify="space-between">
							<Text fontSize="xl" fontWeight="bold">
								Owner Plans
							</Text>
							<Button size="sm" colorScheme="blue" leftIcon={<FiPlus />} onClick={onAddOwnerPlanModalOpen}>
								Add Plan
							</Button>
						</HStack>
					</CardHeader>
					<CardBody>
						<TableContainer>
							<Table variant="simple">
								<Thead>
									<Tr>
										<Th>{t("table.name")}</Th>
										<Th>{t("table.description")}</Th>
										<Th>{t("table.price")}</Th>
										<Th>{t("table.addedDate")}</Th>
										<Th></Th>
									</Tr>
								</Thead>
								<Tbody>
									{customOwnerPlans.length === 0 ? (
										<Tr>
											<Td colSpan={5}>
												<Text textAlign="center" color="gray.500" py={4}>
													No owner plans available. Click "Add Plan" to create one.
												</Text>
											</Td>
										</Tr>
									) : (
										customOwnerPlans.map((row, index) => (
											<CustomOwnerPlansTableRow
												key={index}
												addedDate={row.addedDate}
												id={row.id}
												name={row.name}
												description={row.description}
												price={row.price}
												properties={properties.filter((p) => p.customOwnerPlans.includes(row.id))}
											/>
										))
									)}
								</Tbody>
							</Table>
						</TableContainer>
					</CardBody>
				</Card>
			</Stack>

			{/* Render modals */}
			{renderAddAgentModal()}
			{renderUploadTemplateModal()}
			{renderAddOwmerPlanModal()}
			{setRenderOnBehalfOfModal()}
			{renderAssociationEditModal()}
			{renderProviderModal()}
			<PropertyConfirmModal
				isOpen={isDeleteProviderOpen}
				onClose={() => {
					setProviderToDelete(null);
					onDeleteProviderClose();
				}}
				onConfirm={deleteProvider}
				title={t("property.confirmDelete")}
				message={t("profile.deleteProviderConfirmation")}
				emphasis={t("property.actionCannotBeUndone")}
				isLoading={providerSaving}
			/>
			<PropertyConfirmModal
				isOpen={isDeleteAssociationOpen}
				onClose={() => {
					setAssociationToDelete(null);
					onDeleteAssociationClose();
				}}
				onConfirm={deleteAssociation}
				title={t("property.confirmDelete")}
				message={t("profile.deleteAssociationConfirmation")}
				emphasis={t("property.actionCannotBeUndone")}
				isLoading={associationSaving}
			/>
		</Container>
	);
}

export default Profile;
